import React, { useMemo, useState } from 'react';

import { Select } from '@aline/common';
import { params } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { SearchInput } from 'src/components';
import { NoSearchResultsIcon } from 'src/components/icons/NoSearchResultsIcon';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useGetPlaybooksList } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { AutoGeneratePlaybook } from './AutoGeneratePlaybook';
import { RUN_QUERY_CONTEXT, useRefetchWhileRunIsQuiet } from './playbook-run-hooks';
import { PlaybookDetailPanel } from './PlaybookDetailPanel';
import type { PlaybookCardPlaybook } from './PlaybookListCard';
import { PlaybookListCard } from './PlaybookListCard';

const useStyles = makeStyles()(theme => ({
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  panelContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
}));

export const PlaybooksPanel = React.memo(function PlaybooksPanel(): JSX.Element {
  const { classes } = useStyles();
  const { documentId } = useDocumentParams();
  const [search, setSearch] = useState<string>('');
  /**
   * The open playbook lives in the URL (`?playbookId=`) rather than in state so
   * that links into a specific playbook — chat run cards, mention chips, old
   * messages — land on it directly.
   */
  const { setValue: setOpenPlaybookId, value: openPlaybookId } = useDrawerSearchParam(
    params.rightPanel.playbookId
  );
  /**
   * One operation for the whole list, chips included: each card's status is the
   * playbook's `runStatus` against this document, so opening the panel is a
   * single request rather than one per card.
   */
  const { loading, playbooks, refetch } = useGetPlaybooksList({
    context: RUN_QUERY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { documentId },
  });
  /**
   * A chip reads Running off the last update it heard, and the update that
   * ends a run can be lost like any other. Same safety net as the detail view.
   *
   * Only while the list is what is on screen. A chip says Running for the whole
   * run, so the list never sees a change until the run ends and would refetch
   * on every tick behind an open detail view, which has a safety net of its own.
   */
  useRefetchWhileRunIsQuiet({
    isRunning:
      !openPlaybookId &&
      playbooks.some(playbook => playbook.runStatus === Apollo.PlaybookRunStatus.Running),
    refetch,
    runData: playbooks,
  });
  const searchResults = useMemo(() => {
    const sorted = [...playbooks].sort((a, b) => a.title.localeCompare(b.title));
    type PlaybookKey = keyof (typeof sorted)[number];
    const searchKeys = ['title', 'description'] satisfies PlaybookKey[];
    return matchSorter(sorted, search, { keys: searchKeys });
  }, [playbooks, search]);

  if (openPlaybookId) {
    return (
      <PlaybookDetailPanel
        documentId={documentId}
        playbookId={openPlaybookId}
        onBack={() => setOpenPlaybookId(null)}
      />
    );
  }

  return (
    <div className={classes.panelContainer}>
      <Typography>
        Review contracts quickly and consistently using Aline AI by comparing contracts against your
        playbook.
      </Typography>
      <AutoGeneratePlaybook />
      <SearchInput autoFocus search={search} setSearch={setSearch} />
      <div
        className={classes.cards}
        data-cy={Select.document.editor.rightDrawer.ai.panel.playbookList}
      >
        <PlaybookList
          loading={loading}
          playbooks={searchResults}
          onOpen={playbookId => setOpenPlaybookId(playbookId)}
        />
      </div>
    </div>
  );
});

const useListStyles = makeStyles()(theme => ({
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  loadingCard: {
    borderRadius: 3,
  },
}));

interface PlaybookListProps {
  loading: boolean;
  onOpen: (playbookId: string) => void;
  playbooks: PlaybookCardPlaybook[];
}

function PlaybookList({ loading, onOpen, playbooks }: PlaybookListProps): JSX.Element {
  const { classes } = useListStyles();

  if (loading)
    return (
      <>
        {Array.from({ length: 2 }).map((_, index) => {
          return (
            <Skeleton
              key={index}
              className={classes.loadingCard}
              height={100}
              variant="rectangular"
              width="100%"
            />
          );
        })}
      </>
    );

  if (!playbooks.length) {
    return (
      <div className={classes.empty}>
        <NoSearchResultsIcon />
        <Typography align="center" variant="subtitle2">
          Generate a playbook from this document, or adjust your search.
        </Typography>
      </div>
    );
  }

  return (
    <>
      {playbooks.map(playbook => {
        return (
          <PlaybookListCard
            key={playbook.id}
            playbook={playbook}
            onOpen={() => onOpen(playbook.id)}
          />
        );
      })}
    </>
  );
}
